import React, { useState, useEffect } from "react";
import { Card, Tabs, Typography, Space, Row, Col, Button, Steps } from "antd";

const { Title, Paragraph, Text } = Typography;

const RAGConcept = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [userQuery, setUserQuery] = useState(
    "What's the weather in Tokyo today?"
  );
  const [isAnimating, setIsAnimating] = useState(false);
  const [flowData, setFlowData] = useState({
    showRetrievedData: false,
    showGeneratedResponse: false,
  });

  const demoQueries = [
    {
      query: "What's the weather in Tokyo today?",
      retrieved:
        "Tokyo weather: 22°C, partly cloudy, humidity 65%, forecast from weather.com updated 2 hours ago",
      response:
        "The weather in Tokyo today is 22°C (72°F) and partly cloudy with 65% humidity. It's a pleasant day!",
    },
    {
      query: "What are our Q4 sales numbers?",
      retrieved:
        "Company internal report: Q4 2024 sales reached $2.3M, up 18% from Q3, top product: CloudSync Pro",
      response:
        "Your Q4 2024 sales reached $2.3 million, which is an 18% increase from Q3. CloudSync Pro was your top-performing product!",
    },
    {
      query: "Explain photosynthesis",
      retrieved:
        "From biology textbook: Photosynthesis converts light energy into chemical energy, plants use chlorophyll, produces oxygen and glucose",
      response:
        "Photosynthesis is how plants convert sunlight into food! They use chlorophyll (the green pigment) to turn light energy, water, and CO2 into glucose (sugar) and oxygen.",
    },
  ];

  const runDemo = () => {
    setIsAnimating(true);
    setActiveStep(0);
    setFlowData({
      showRetrievedData: false,
      showGeneratedResponse: false,
    });

    setTimeout(() => setActiveStep(1), 500);
    setTimeout(() => setActiveStep(2), 2000);
    setTimeout(() => setActiveStep(3), 3500);
    setTimeout(() => {
      setActiveStep(3);
      setFlowData((prev) => ({ ...prev, showRetrievedData: true }));
    }, 5000);
    setTimeout(() => setActiveStep(4), 6500);
    setTimeout(() => {
      setActiveStep(5);
      setFlowData((prev) => ({ ...prev, showGeneratedResponse: true }));
    }, 8000);
    setTimeout(() => setIsAnimating(false), 10000);
  };

  const renderIntro = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title={<span style={{ color: "#0071e3" }}>What is RAG?</span>}>
        <Paragraph style={{ fontSize: "16px" }}>
          Imagine you're taking a test and you're allowed to check your notes!{" "}
          <Text strong>RAG (Retrieval-Augmented Generation)</Text> works the
          same way. Instead of just relying on what the AI already knows, it
          first <Text strong>retrieves</Text> (looks up) relevant information
          from external sources, then uses that information to{" "}
          <Text strong>generate</Text> a better, more accurate answer.
        </Paragraph>
      </Card>

      <Card
        title={<span style={{ color: "#0071e3" }}>Think of it like...</span>}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div
              style={{
                padding: "16px",
                borderRadius: "8px",
                border: "1px solid #0071e3",
              }}
            >
              <Paragraph style={{ fontWeight: 600, marginBottom: "8px" }}>
                Without RAG
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                Like answering questions just from memory by recalling learned
                things
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div
              style={{
                padding: "16px",
                borderRadius: "8px",
                border: "1px solid #0071e3",
              }}
            >
              <Paragraph style={{ fontWeight: 600, marginBottom: "8px" }}>
                With RAG
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                Like being able to quickly Google or check the textbook before
                answering
              </Paragraph>
            </div>
          </Col>
        </Row>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>The Three Steps</span>}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "#0071e3",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "white",
                  margin: "0 auto 16px",
                }}
              >
                1
              </div>
              <Title level={4}>Question</Title>
              <Paragraph>
                You ask the AI a question - this could be anything from "What's
                the weather?" to "What did the CEO say in last week's meeting?"
              </Paragraph>
            </div>
          </Col>

          <Col xs={24} md={8}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "#0071e3",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "white",
                  margin: "0 auto 16px",
                }}
              >
                2
              </div>
              <Title level={4}>Retrieve</Title>
              <Paragraph>
                The AI searches through documents, databases, websites, or files
                to find relevant information. Like a super-fast librarian!
              </Paragraph>
            </div>
          </Col>

          <Col xs={24} md={8}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "#0071e3",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "white",
                  margin: "0 auto 16px",
                }}
              >
                3
              </div>
              <Title level={4}>Generate</Title>
              <Paragraph>
                Using the retrieved information + its own knowledge, the AI
                generates a complete, accurate answer customized for you!
              </Paragraph>
            </div>
          </Col>
        </Row>
      </Card>
    </Space>
  );

  const renderDemo = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>Watch RAG Flow In Action</span>
        }
      >
        <Paragraph style={{ marginBottom: "16px" }}>
          Select a question and click "Run Demo" to see how RAG works step by
          step:
        </Paragraph>

        <Space wrap style={{ marginBottom: "16px" }}>
          {demoQueries.map((item, index) => (
            <Button
              key={index}
              type={userQuery === item.query ? "primary" : "default"}
              onClick={() => {
                setUserQuery(item.query);
                setActiveStep(0);
                setFlowData({
                  showRetrievedData: false,
                  showGeneratedResponse: false,
                });
              }}
            >
              Query {index + 1}
            </Button>
          ))}
        </Space>

        <div
          style={{
            padding: "16px",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <Text strong>Selected Question: </Text>
          <Text>{userQuery}</Text>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            type="primary"
            onClick={runDemo}
            disabled={isAnimating}
            size="large"
            style={{ marginBottom: "24px" }}
          >
            {isAnimating ? "Running..." : "Run Demo"}
          </Button>
        </div>

        <Steps
          current={activeStep}
          direction="vertical"
          style={{ marginTop: "24px" }}
        >
          <Steps.Step
            title="User Question"
            description={activeStep >= 1 ? userQuery : "Waiting..."}
          />
          <Steps.Step
            title="AI Analysis"
            description={
              activeStep >= 2
                ? "Understanding the question and identifying what information is needed"
                : "Waiting..."
            }
          />
          <Steps.Step
            title="RAG Retrieval"
            description={
              flowData.showRetrievedData
                ? demoQueries.find((q) => q.query === userQuery)?.retrieved
                : activeStep >= 3
                ? "Searching knowledge base..."
                : "Waiting..."
            }
          />
          <Steps.Step
            title="AI Processing"
            description={
              activeStep >= 4
                ? "Processing retrieved data and generating response"
                : "Waiting..."
            }
          />
          <Steps.Step
            title="Final Response"
            description={
              flowData.showGeneratedResponse
                ? demoQueries.find((q) => q.query === userQuery)?.response
                : activeStep >= 5
                ? "Generating response..."
                : "Waiting..."
            }
          />
        </Steps>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Visual Comparison</span>}>
        <Row gutter={[24, 24]} align="middle">
          {/* Traditional AI Model */}
          <Col xs={24} md={12}>
            <div
              style={{
                padding: "32px",
                border: "2px solid #7e7575ff",
                borderRadius: "12px",
                textAlign: "center",
                backgroundColor: "#fafafa",
                height: "420px",
              }}
            >
              <Title level={4} style={{ marginBottom: "16px" }}>
                Traditional AI
              </Title>
              <div
                style={{
                  padding: "24px",
                  backgroundColor: "#fff",
                  border: "1px solid #0071e3",
                  borderRadius: "8px",
                  color: "black",
                  fontSize: "18px",
                  fontWeight: 600,
                  marginBottom: "12px",
                }}
              >
                GPT / Claude / Gemini
              </div>
              <Paragraph style={{ marginBottom: 0 }}>
                Relies only on training data (limited & outdated)
              </Paragraph>
            </div>
          </Col>

          {/* RAG-Enhanced Model */}
          <Col xs={24} md={12}>
            <div
              style={{
                padding: "32px",
                border: "2px solid #0071e3",
                borderRadius: "12px",
                textAlign: "center",
                backgroundColor: "#f0f5ff",
                height: "420px",
              }}
            >
              <Title level={4} style={{ marginBottom: "16px" }}>
                RAG-Enhanced AI
              </Title>
              <div
                style={{
                  padding: "24px",
                  backgroundColor: "#fff",
                  border: "1px solid #0071e3",
                  borderRadius: "8px",
                  color: "black",
                  fontSize: "18px",
                  fontWeight: 600,
                  marginBottom: "12px",
                }}
              >
                GPT / Claude / Gemini
              </div>
              <div style={{ fontSize: "24px", margin: "8px 0" }}>+</div>
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "white",
                  borderRadius: "8px",
                  border: "1px solid #0071e3",
                }}
              >
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    marginBottom: "12px",
                    // color: "#0071e3",
                  }}
                >
                  RAG System
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    fontSize: "24px",
                  }}
                >
                  <span>📄</span>
                  <span>📊</span>
                  <span>🗄️</span>
                  <span>🌐</span>
                  <span>📚</span>
                </div>
                <Paragraph
                  type="secondary"
                  style={{
                    fontSize: "12px",
                    marginTop: "8px",
                    marginBottom: 0,
                  }}
                >
                  Documents • Databases • Web • Knowledge Base
                </Paragraph>
              </div>
              <Paragraph style={{ marginTop: "16px", marginBottom: 0 }}>
                Can access current, specific information!
              </Paragraph>
            </div>
          </Col>
        </Row>
      </Card>
    </Space>
  );

  // const renderWhyRAG = () => (
  //   <Space direction="vertical" size="large" style={{ width: "100%" }}>
  //     <Row gutter={[16, 16]}>
  //       <Col xs={24} md={12}>
  //         <Card
  //           title={
  //             <span style={{ color: "#0071e3" }}>Problems Without RAG</span>
  //           }
  //         >
  //           <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
  //             <li style={{ marginBottom: "8px" }}>
  //               <Text strong>Outdated Info:</Text> AI training data has a cutoff
  //               date
  //             </li>
  //             <li style={{ marginBottom: "8px" }}>
  //               <Text strong>No Company Data:</Text> Can't access your private
  //               documents
  //             </li>
  //             <li style={{ marginBottom: "8px" }}>
  //               <Text strong>Makes Things Up:</Text> "Hallucinates" when unsure
  //             </li>
  //             <li style={{ marginBottom: 0 }}>
  //               <Text strong>Generic Answers:</Text> Lacks specific, current
  //               details
  //             </li>
  //           </ul>
  //         </Card>
  //       </Col>

  //       <Col xs={24} md={12}>
  //         <Card
  //           title={<span style={{ color: "#0071e3" }}>Benefits With RAG</span>}
  //         >
  //           <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
  //             <li style={{ marginBottom: "8px" }}>
  //               <Text strong>Always Current:</Text> Pulls latest information in
  //               real-time
  //             </li>
  //             <li style={{ marginBottom: "8px" }}>
  //               <Text strong>Company Knowledge:</Text> Access your private data
  //               securely
  //             </li>
  //             <li style={{ marginBottom: "8px" }}>
  //               <Text strong>Fact-Based:</Text> Cites actual sources, less
  //               hallucination
  //             </li>
  //             <li style={{ marginBottom: 0 }}>
  //               <Text strong>Specific Answers:</Text> Detailed, accurate,
  //               contextual responses
  //             </li>
  //           </ul>
  //         </Card>
  //       </Col>
  //     </Row>
  //   </Space>
  // );

  const renderWhyRAG = () => {
  const comparisonData = [
    {
      problem: "Outdated Information",
      withoutRAG: "AI training data has a cutoff date",
      withRAG: "Pulls latest information in real-time",
    },
    {
      problem: "Company Data Access",
      withoutRAG: "Can't access your private documents",
      withRAG: "Access your private data securely",
    },
    {
      problem: "Accuracy Issues",
      withoutRAG: "\"Hallucinates\" when unsure",
      withRAG: "Cites actual sources, less hallucination",
    },
    {
      problem: "Response Quality",
      withoutRAG: "Generic answers lacking specifics",
      withRAG: "Detailed, accurate, contextual responses",
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Desktop Table View */}
      <div className="desktop-table">
        <Card title={<span style={{ color: "#0071e3" }}>RAG: Problems vs Benefits</span>}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "2px solid #0071e3"}}>
                  <th style={{ padding: "2px 16px", textAlign: "left", width: "30%" }}>
                    <Text strong>Problem Area</Text>
                  </th>
                  <th style={{ padding: "2px 16px", textAlign: "left", width: "35%" }}>
                    <Text strong>Without RAG</Text>
                  </th>
                  <th style={{ padding: "2px 16px", textAlign: "left", width: "35%" }}>
                    <Text strong>With RAG</Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: idx < comparisonData.length - 1 ? "2px solid #f0f0f0" : "none" }}>
                    <td style={{ padding: "16px", verticalAlign: "top", textAlign: "left" }}>
                      <Text strong style={{ color: "#0071e3" }}>{row.problem}</Text>
                    </td>
                    <td style={{ padding: "16px", textAlign: "left", verticalAlign: "top" }}>
                      {row.withoutRAG}
                    </td>
                    <td style={{ padding: "16px", textAlign: "left", verticalAlign: "top" }}>
                      {row.withRAG}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Mobile Card View */}
      <div className="mobile-cards">
        <Card title={<span style={{ color: "#0071e3" }}>RAG: Problems vs Benefits</span>}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {comparisonData.map((item, idx) => (
              <div key={idx} style={{ 
                border: "1px solid #0071e3",
                borderRadius: "8px",
                padding: "16px",
                backgroundColor: idx % 2 === 0 ? "#fafafa" : "white"
              }}>
                <Text strong style={{ color: "#0071e3", fontSize: "16px" }}>{item.problem}</Text>
                
                <div style={{ marginTop: "12px", padding: "4px", borderRadius: "6px" }}>
                  <Text strong>Without RAG: </Text>
                  <Text>{item.withoutRAG}</Text>
                </div>
                
                <div style={{ marginTop: "8px", padding: "4px", borderRadius: "6px" }}>
                  <Text strong>With RAG: </Text>
                  <Text>{item.withRAG}</Text>
                </div>
              </div>
            ))}
          </Space>
        </Card>
      </div>

      <style>{`
        @media (min-width: 769px) {
          .desktop-table {
            display: block;
          }
          .mobile-cards {
            display: none;
          }
        }
        @media (max-width: 768px) {
          .desktop-table {
            display: none;
          }
          .mobile-cards {
            display: block;
          }
        }
      `}</style>
    </Space>
  );
};

  const renderRealWorld = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card
            title={<span style={{ color: "#0071e3" }}>Customer Support</span>}
          >
            <Paragraph style={{ marginBottom: 0 }}>
              AI chatbots that can look up order history, product manuals, and
              company policies to give accurate answers instead of generic
              responses.
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title={
              <span style={{ color: "#0071e3" }}>Research Assistants</span>
            }
          >
            <Paragraph style={{ marginBottom: 0 }}>
              Tools that search through thousands of scientific papers, legal
              documents, or medical records to find relevant information and
              summarize findings.
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title={<span style={{ color: "#0071e3" }}>Enterprise Search</span>}
          >
            <Paragraph style={{ marginBottom: 0 }}>
              Internal company AI that can search emails, Slack messages, Google
              Docs, and databases to answer employee questions instantly.
            </Paragraph>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card
            title={
              <span style={{ color: "#0071e3" }}>Education & Learning</span>
            }
          >
            <Paragraph style={{ marginBottom: 0 }}>
              AI tutors that can reference textbooks, course materials, and
              academic papers to provide accurate, curriculum-aligned
              explanations.
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title={<span style={{ color: "#0071e3" }}>News & Content</span>}
          >
            <Paragraph style={{ marginBottom: 0 }}>
              AI systems that pull the latest news articles, reports, and
              updates to provide current information on any topic.
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title={<span style={{ color: "#0071e3" }}>Banking Systems</span>}
          >
            <Paragraph style={{ marginBottom: 0 }}>
              AI retrieves transaction logs, compliance rules, and customer
              history to generate fraud and risk reports.
            </Paragraph>
          </Card>
        </Col>
      </Row>

      <Card title={<span style={{ color: "#0071e3" }}>Popular RAG Tools</span>}>
        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li style={{ marginBottom: "8px" }}>
            <Text strong>ChatGPT with Web Browsing:</Text> Can search the
            internet for current information
          </li>
          <li style={{ marginBottom: "8px" }}>
            <Text strong>Perplexity AI:</Text> Search engine + AI that cites
            sources
          </li>
          <li style={{ marginBottom: "8px" }}>
            <Text strong>Microsoft Copilot:</Text> RAG with your Office
            documents and emails
          </li>
          <li style={{ marginBottom: "8px" }}>
            <Text strong>Notion AI:</Text> Searches your Notion workspace to
            answer questions
          </li>
          <li style={{ marginBottom: 0 }}>
            <Text strong>Google's AI Overviews:</Text> Search results enhanced
            with AI summaries
          </li>
        </ul>
      </Card>
    </Space>
  );

  const tabItems = [
    {
      key: "intro",
      label: "What is RAG?",
      children: renderIntro(),
    },
    {
      key: "demo",
      label: "Interactive Demo",
      children: renderDemo(),
    },
    {
      key: "why",
      label: "Why RAG?",
      children: renderWhyRAG(),
    },
    {
      key: "realworld",
      label: "Real World Uses",
      children: renderRealWorld(),
    },
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "32px" }}
        className="fade-in-down"
      >
        <Title level={1}>Retrieval-Augmented Generation (RAG)</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          How AI gets smarter by looking things up before answering!
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="intro" items={tabItems} size="large" />
    </div>
  );
};

export default RAGConcept;
